import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import SubHeader from '../layout/components/SubHeader';
import TemplateSelectionStep from './TemplateSelectionStep';
import CampaignInformationStep from './CampaignInformationStep';
import SubjectLineStep from './SubjectLineStep';
import EmailContentStep from './EmailContentStep';
import MessageContentStep from './MessageContentStep';
import PatientListStep from './PatientListStep';
import CompleteStep from './CompleteStep';
import useEmailEditorStore from '../helpers/email-editor/utilities/emailEditorStore';
import { htmlExport } from '../helpers/email-editor/utilities/htmlExport';
import './CampaignWizard.css';

function CampaignWizard({
    title,
    description,
    icon = 'bi-megaphone',
    navigatePath,
    campaignType,
    entityLabel = 'Campaign',
    patientCampaignType = 'blast',
    steps = [],
    onSubmit,
}) {
    const [currentStep, setCurrentStep] = useState(1);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [formData, setFormData] = useState({
        campaignName: '',
        description: '',
        subjectLine: '',
        content: '',
        messageContent: '',
        selectedPatients: [],
    });
    const [templateOption, setTemplateOption] = useState('blank');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const emailSaveFnRef = useRef(null);

    const isLastStep = currentStep >= steps.length;

    const handleNext = () => {
        if (currentStep < steps.length) {
            const step = steps.find((s) => s.id === currentStep);
            if (step?.component === 'TemplateSelection' && templateOption === 'template' && !selectedTemplate) {
                toast.error('Select a campaign template, or choose Start Blank.');
                return;
            }
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (submitting) {
            return;
        }
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        } else {
            window.history.back();
        }
    };

    const handleStepSelect = (stepId) => {
        if (submitting) {
            return;
        }
        if (!steps.some((step) => step.id === stepId)) {
            return;
        }
        setCurrentStep(stepId);
    };

    const handleSubmit = async () => {
        if (!onSubmit || submitting) {
            return;
        }

        setSubmitting(true);
        try {
            let payload = { ...formData };

            if (campaignType === 'email') {
                let htmlContent = '';
                let jsonContent = null;

                if (emailSaveFnRef.current) {
                    const exported = await emailSaveFnRef.current();
                    htmlContent = exported?.htmltemplate || '';
                    jsonContent = exported?.template || null;
                } else {
                    const state = useEmailEditorStore.getState();
                    htmlContent = htmlExport(
                        state.blocks,
                        state.globalProperties,
                        formData.subjectLine || 'Email Template'
                    );
                    jsonContent = state.exportTemplate();
                }

                payload = {
                    ...payload,
                    htmlContent,
                    jsonContent,
                };
            }

            await onSubmit(payload);
        } catch (error) {
            // Parent shows toast; keep user on the step to correct and retry.
        } finally {
            setSubmitting(false);
        }
    };

    const handleTemplateApplied = (template) => {
        if (!template) {
            setEmailTemplate(null);
            setSelectedTemplate(null);
            return;
        }

        setSelectedTemplate(template);

        const jsonContent = template.jsonContent;
        if (jsonContent) {
            try {
                const parsed =
                    typeof jsonContent === 'string' ? JSON.parse(jsonContent) : jsonContent;
                setEmailTemplate(parsed);
            } catch (error) {
                console.error('Invalid template jsonContent', error);
                setEmailTemplate(null);
                toast.error('Selected template has invalid design data.');
            }
        } else {
            setEmailTemplate(null);
        }

        setFormData((prev) => ({
            ...prev,
            campaignName: prev.campaignName?.trim() ? prev.campaignName : template.name || prev.campaignName,
        }));
    };

    const renderStepContent = () => {
        const step = steps.find((s) => s.id === currentStep);
        if (!step) return null;

        switch (step.component) {
            case 'TemplateSelection':
                return (
                    <TemplateSelectionStep
                        templateOption={templateOption}
                        setTemplateOption={setTemplateOption}
                        templates={templates}
                        setTemplates={setTemplates}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={setSelectedTemplate}
                        loadingTemplates={loadingTemplates}
                        setLoadingTemplates={setLoadingTemplates}
                        templateType={campaignType === 'sms' ? 'SMS' : 'EMAIL'}
                        onTemplateApplied={handleTemplateApplied}
                    />
                );
            case 'CampaignInformation':
                return (
                    <CampaignInformationStep
                        formData={formData}
                        setFormData={setFormData}
                        entityLabel={entityLabel}
                    />
                );
            case 'SubjectLine':
                return <SubjectLineStep formData={formData} setFormData={setFormData} />;
            case 'EmailContent':
                return (
                    <EmailContentStep
                        emailTemplate={emailTemplate}
                        subjectLine={formData.subjectLine}
                        selectedTemplateName={selectedTemplate?.name}
                        editorKey={selectedTemplate?.id || 'blank-email'}
                        saveemail={(saveFn) => {
                            emailSaveFnRef.current = saveFn;
                        }}
                    />
                );
            case 'MessageContent':
                return <MessageContentStep formData={formData} setFormData={setFormData} />;
            case 'PatientList':
                return (
                    <PatientListStep
                        formData={formData}
                        setFormData={setFormData}
                        contactField={campaignType === 'email' ? 'email' : 'phone'}
                        patientCampaignType={patientCampaignType}
                        entityLabel={entityLabel}
                    />
                );
            case 'Complete': {
                const additionalFields = [];
                if (campaignType === 'email') {
                    additionalFields.push({ label: 'Subject Line', value: formData.subjectLine });
                    if (selectedTemplate?.name) {
                        additionalFields.push({ label: 'Template', value: selectedTemplate.name });
                    }
                } else {
                    additionalFields.push({
                        label: 'Message Length',
                        value: `${formData.messageContent.length} characters`,
                    });
                }
                return (
                    <CompleteStep
                        formData={formData}
                        additionalFields={additionalFields}
                        entityLabel={entityLabel}
                        campaignType={campaignType}
                    />
                );
            }
            default:
                return null;
        }
    };

    return (
        <div className="ecx-page ecx-page--full-width ecx-campaign-wizard">
            <SubHeader
                title={title}
                description={description}
                icon={icon}
                containerClass="w-100 px-4"
            />

            <div className="ecx-page-body">
                <div className="ecx-campaign-wizard__steps ecx-page-card mb-3">
                    <div
                        className="ecx-campaign-wizard__steps-track"
                        role="tablist"
                        aria-label={`${entityLabel} steps`}
                    >
                        {steps.map((step, index) => {
                            const isComplete = currentStep > step.id;
                            const isActive = currentStep === step.id;

                            return (
                                <React.Fragment key={step.id}>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        aria-controls={`campaign-step-panel-${step.id}`}
                                        id={`campaign-step-tab-${step.id}`}
                                        tabIndex={0}
                                        className={`ecx-campaign-wizard__step ${
                                            isComplete ? 'is-complete' : ''
                                        } ${isActive ? 'is-active' : ''}`}
                                        onClick={() => handleStepSelect(step.id)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleStepSelect(step.id);
                                                return;
                                            }

                                            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
                                                return;
                                            }

                                            event.preventDefault();
                                            const direction = event.key === 'ArrowRight' ? 1 : -1;
                                            const nextIndex = Math.min(
                                                Math.max(index + direction, 0),
                                                steps.length - 1
                                            );
                                            handleStepSelect(steps[nextIndex].id);
                                            requestAnimationFrame(() => {
                                                document
                                                    .getElementById(
                                                        `campaign-step-tab-${steps[nextIndex].id}`
                                                    )
                                                    ?.focus();
                                            });
                                        }}
                                    >
                                        <span
                                            className="ecx-campaign-wizard__step-index"
                                            aria-hidden="true"
                                        >
                                            {isComplete ? <i className="bi bi-check-lg" /> : step.id}
                                        </span>
                                        <span className="ecx-campaign-wizard__step-copy">
                                            <span className="ecx-campaign-wizard__step-title">
                                                {step.title}
                                            </span>
                                            <span className="ecx-campaign-wizard__step-subtitle">
                                                {step.subtitle}
                                            </span>
                                        </span>
                                    </button>
                                    {index < steps.length - 1 && (
                                        <div
                                            className={`ecx-campaign-wizard__connector ${
                                                isComplete ? 'is-complete' : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                <div className="ecx-page-card ecx-campaign-wizard__content">
                    <div
                        className="ecx-page-panel__body ecx-campaign-wizard__content-body"
                        role="tabpanel"
                        id={`campaign-step-panel-${currentStep}`}
                        aria-labelledby={`campaign-step-tab-${currentStep}`}
                    >
                        {renderStepContent()}
                    </div>

                    <div className="ecx-campaign-wizard__footer">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleBack}
                            disabled={submitting}
                        >
                            {currentStep === 1 ? 'Cancel' : 'Back'}
                        </button>
                        {isLastStep ? (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Launching...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-send me-1" aria-hidden="true" />
                                        Launch {entityLabel}
                                    </>
                                )}
                            </button>
                        ) : (
                            <button type="button" className="btn btn-primary" onClick={handleNext}>
                                Next
                                <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CampaignWizard;
